import React, {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Card } from "../types";
import { Button } from "@/components/ui/button";
import { svgBase64 } from "@/utils/svg-base-64";
import { IconMapPinSingle } from "@/assets/icons/icon-map-pin-single";
import { IconMapPinMultiple } from "@/assets/icons/icon-map-pin-multiple";
import { IconMapPinPartnerSingle } from "@/assets/icons/icon-map-pin-partner-single";
import { IconMapPinPartnerMultiple } from "@/assets/icons/icon-map-pin-partner-multiple";
import { useSelection } from "../contexts/SelectionContext";
import { isCardUnavailable, isCardOutdated } from "../utils/card-status";
import { Building2 } from "lucide-react";
import { spaceService } from "@/services/space-service";

interface Vision360MapProps {
  cards: Card[];
  spacesWithRoomsIds?: number[];
  spaceRoomCounts?: Record<number, number>;
  onOpenDrawer: (card: Card) => void;
  customBottomPosition?: number;
  onMarkerClick?: (card: Card) => void;
  centerCoords?: {
    lat: number;
    lng: number;
  } | null;
  includeRecentUpdates?: boolean;
  locationFilters?: {
    latitude?: number;
    longitude?: number;
    distance?: number;
  };
}

export interface Vision360MapRef {
  centerOnCard: (card: Card) => void;
}

interface GroupedMarker {
  id: string;
  position: { lat: number; lng: number };
  cards: Card[];
  count: number;
}

export const Vision360Map = forwardRef<Vision360MapRef, Vision360MapProps>(
  (
    {
      cards,
      spacesWithRoomsIds = [],
      spaceRoomCounts = {},
      onOpenDrawer,
      customBottomPosition,
      onMarkerClick,
      centerCoords,
      includeRecentUpdates = false,
      locationFilters,
    },
    ref,
  ) => {
    const { isLoaded } = useJsApiLoader({
      id: "google-map-script",
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY!,
    });
    const { isSelected, toggleSelection } = useSelection();
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [groupedMarkers, setGroupedMarkers] = useState<GroupedMarker[]>([]);
    const [selectedMarker, setSelectedMarker] = useState<GroupedMarker | null>(
      null,
    );
    const [showScrollIndicators, setShowScrollIndicators] = useState({
      left: false,
      right: false,
    });
    const [animatedMarkerId, setAnimatedMarkerId] = useState<string | null>(
      null,
    );
    const [showCenterInfoWindow, setShowCenterInfoWindow] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [viewMode, setViewMode] = useState<"rooms" | "partners">("rooms");
    const [partnersData, setPartnersData] = useState<
      Array<{
        spaceId: number;
        name: string;
        address: string;
        neighborhood: string | null;
        city: string;
        latitude: number;
        longitude: number;
        enabled: boolean;
        type: number;
        admMail?: string | null;
        hasMeetingRoomPartnership?: boolean;
        privateOfficesCount?: number;
      }>
    >([]);
    const [loadingPartners, setLoadingPartners] = useState(false);

    const handleMapLoad = useCallback((map: google.maps.Map) => {
      setMap(map);

      const handleZoomChanged = () => {
        setHasUserInteracted(true);
      };

      const handleDragEnd = () => {
        setHasUserInteracted(true);
      };

      map.addListener("zoom_changed", handleZoomChanged);
      map.addListener("dragend", handleDragEnd);

      setTimeout(() => {
        setMapReady(true);
      }, 100);
    }, []);

    const handleTogglePartners = useCallback(async () => {
      if (viewMode === "rooms") {
        setLoadingPartners(true);
        try {
          let filters = locationFilters;
          if (!filters?.latitude || !filters?.longitude) {
            if (cards.length > 0) {
              const avgLat =
                cards.reduce((sum, card) => sum + card.coords.lat, 0) /
                cards.length;
              const avgLng =
                cards.reduce((sum, card) => sum + card.coords.lng, 0) /
                cards.length;
              filters = {
                latitude: avgLat,
                longitude: avgLng,
                distance: 50,
              };
            } else {
              setLoadingPartners(false);
              return;
            }
          } else if (!filters.distance) {
            filters = {
              ...filters,
              distance: 30,
            };
          }

          const spacesListResponse =
            await spaceService.getSpacesByIds(spacesWithRoomsIds);

          const partnersWithRooms = spacesListResponse.data.map((space) => ({
            spaceId: space.id,
            name: space.name,
            address: space.address,
            neighborhood: space.neighborhood,
            city: space.city,
            latitude: space.latitude,
            longitude: space.longitude,
            enabled: space.enabled,
            type: space.type,
            privateOfficesCount: spaceRoomCounts[space.id] || 0,
          }));

          const spacesWithoutRooms =
            await spaceService.getSpacesWithoutPrivateOffices({
              ...filters,
              excludeSpaceIds: spacesWithRoomsIds,
            });

          const allPartners = [
            ...partnersWithRooms,
            ...spacesWithoutRooms.results.map((s) => ({
              ...s,
              privateOfficesCount: 0,
            })),
          ];

          setPartnersData(allPartners);
          setViewMode("partners");
        } catch (error) {
          // Error handled silently
        } finally {
          setLoadingPartners(false);
        }
      } else {
        setViewMode("rooms");
        setSelectedMarker(null);
      }
    }, [viewMode, locationFilters, cards]);

    const centerOnCard = useCallback(
      (card: Card) => {
        if (!map || !card.coords?.lat || !card.coords?.lng) return;

        const position = {
          lat: card.coords.lat,
          lng: card.coords.lng,
        };

        map.setCenter(position);
        map.setZoom(16);
        map.panTo(position);

        setAnimatedMarkerId(card.id);
        setTimeout(() => {
          setAnimatedMarkerId(null);
        }, 3000);
      },
      [map],
    );

    useImperativeHandle(
      ref,
      () => ({
        centerOnCard,
      }),
      [centerOnCard],
    );

    const handleMarkerClick = useCallback(
      (groupedMarker: GroupedMarker) => {
        setSelectedMarker(groupedMarker);
        if (onMarkerClick && groupedMarker.cards.length > 0) {
          const card = groupedMarker.cards[0];
          const shouldPreventScroll =
            isCardOutdated(card) && !includeRecentUpdates;

          // Only trigger scroll if the asset is not outdated OR if filter is checked
          if (!shouldPreventScroll) {
            onMarkerClick(card);
          }
        }
      },
      [onMarkerClick, includeRecentUpdates],
    );

    const handleCloseCardList = () => {
      setSelectedMarker(null);
      setShowScrollIndicators({ left: false, right: false });
    };

    const updateScrollIndicators = useCallback((container: HTMLElement) => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowScrollIndicators({
        left: scrollLeft > 0,
        right: scrollLeft < scrollWidth - clientWidth - 1,
      });
    }, []);

    const scrollTo = useCallback((direction: "left" | "right") => {
      const container = document.querySelector(
        `.vision360-map-cards`,
      ) as HTMLElement;
      if (container) {
        const scrollAmount = 320;
        const newScrollLeft =
          direction === "left"
            ? container.scrollLeft - scrollAmount
            : container.scrollLeft + scrollAmount;

        container.scrollTo({
          left: newScrollLeft,
          behavior: "smooth",
        });
      }
    }, []);

    const groupCardsByCoordinates = useCallback(
      (cards: Card[]): GroupedMarker[] => {
        const groupedMap = new Map<string, Card[]>();

        const validCards = cards.filter(
          (card) =>
            card.coords?.lat != null &&
            card.coords?.lng != null &&
            !isNaN(card.coords.lat) &&
            !isNaN(card.coords.lng) &&
            card.coords.lat !== 0 &&
            card.coords.lng !== 0,
        );

        validCards.forEach((card) => {
          const lat = card.coords!.lat;
          const lng = card.coords!.lng;
          const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

          if (!groupedMap.has(key)) {
            groupedMap.set(key, []);
          }
          groupedMap.get(key)!.push(card);
        });

        const result = Array.from(groupedMap.values()).map((cards, index) => {
          const firstCard = cards[0];
          const lat = firstCard.coords!.lat;
          const lng = firstCard.coords!.lng;

          return {
            id: `marker-${lat.toFixed(6)}-${lng.toFixed(6)}-${index}`,
            position: { lat, lng },
            cards,
            count: cards.length,
          };
        });

        return result;
      },
      [],
    );

    const getIconWithAnimation = useCallback(
      (groupedMarker: GroupedMarker) => {
        const allUnavailableOrOutdated = groupedMarker.cards.every((card) => {
          return isCardUnavailable(card) || isCardOutdated(card);
        });

        const isPartnerMode = viewMode === "partners";

        if (groupedMarker.count > 1) {
          return isPartnerMode
            ? IconMapPinPartnerMultiple(
                groupedMarker.count,
                false,
                allUnavailableOrOutdated,
              )
            : IconMapPinMultiple(
                groupedMarker.count,
                false,
                allUnavailableOrOutdated,
              );
        } else {
          return isPartnerMode
            ? IconMapPinPartnerSingle(false, allUnavailableOrOutdated)
            : IconMapPinSingle(false, allUnavailableOrOutdated);
        }
      },
      [viewMode],
    );

    const getMarkerIcon = useCallback(
      (groupedMarker: GroupedMarker, shouldAnimate: boolean) => {
        const svg = getIconWithAnimation(groupedMarker);
        const url = svgBase64(svg);
        const icon = {
          url,
          scale: shouldAnimate ? 1.2 : 1,
          anchor: new window.google.maps.Point(25, 25),
        };
        return icon;
      },
      [getIconWithAnimation],
    );

    const getCenterMarkerIcon = useCallback(() => {
      const centerPinSvg = `
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="#FFFFFF" stroke-width="2"/>
          <circle cx="20" cy="20" r="8" fill="#FFFFFF"/>
          <circle cx="20" cy="20" r="4" fill="#3B82F6"/>
        </svg>
      `;
      return {
        url: svgBase64(centerPinSvg),
        scale: 1,
        anchor: new window.google.maps.Point(20, 20),
      };
    }, []);

    const handleCenterMarkerClick = useCallback(() => {
      setShowCenterInfoWindow(true);
    }, []);

    const partnersCards = useMemo((): Card[] => {
      const cards = partnersData.map((partner) => ({
        id: `partner-${partner.spaceId}`,
        search_id: partner.spaceId,
        title: partner.name,
        address: `${partner.address}, ${partner.neighborhood}`,
        coords: {
          lat: partner.latitude,
          lng: partner.longitude,
        },
        status: 999,
        capacity: 0,
        monthly_price: 0,
        credits: 0,
        images: [],
        original_url: "",
        updated_at: new Date().toISOString(),
        space: {
          name: partner.name,
          id: String(partner.spaceId),
        },
        days_since_update: undefined,
        privateOfficesCount: partner.privateOfficesCount || 0,
        space_details: {
          id: partner.spaceId,
          name: partner.name,
          cityname: partner.city,
          neighborhood: partner.neighborhood,
          address: partner.address,
          latitude: String(partner.latitude),
          longitude: String(partner.longitude),
        },
      }));

      return cards;
    }, [partnersData]);

    const groupedMarkersMemo = useMemo(() => {
      const cardsToGroup = viewMode === "partners" ? partnersCards : cards;
      const grouped = groupCardsByCoordinates(cardsToGroup);
      return grouped;
    }, [cards, partnersCards, viewMode, groupCardsByCoordinates]);

    const initialCenter = useMemo(() => {
      if (groupedMarkersMemo.length > 0) {
        return groupedMarkersMemo[0].position;
      }
      if (centerCoords) {
        return centerCoords;
      }
      return { lat: -23.5505, lng: -46.6333 };
    }, [groupedMarkersMemo, centerCoords]);

    useEffect(() => {
      if (cards.length > 0) {
        setGroupedMarkers(groupedMarkersMemo);
      } else {
        setGroupedMarkers([]);
      }
    }, [groupedMarkersMemo, cards.length]);

    useEffect(() => {
      if (
        map &&
        (groupedMarkers.length > 0 || centerCoords) &&
        !hasUserInteracted
      ) {
        const bounds = new window.google.maps.LatLngBounds();

        groupedMarkers.forEach((groupedMarker) => {
          bounds.extend(groupedMarker.position);
        });

        if (centerCoords) {
          bounds.extend(centerCoords);
        }

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      }
    }, [map, groupedMarkers, centerCoords, hasUserInteracted]);

    useEffect(() => {
      setSelectedMarker(null);
      setHasUserInteracted(false);
      if (viewMode === "partners") {
        setViewMode("rooms");
      }
    }, [cards]);

    useEffect(() => {
      if (!selectedMarker) return;

      const handleWheel = (e: Event) => {
        const wheelEvent = e as WheelEvent;
        const target = e.currentTarget as HTMLElement;
        if (
          target.classList.contains("vision360-map-cards") &&
          target.style.overflowX === "auto"
        ) {
          e.preventDefault();

          const scrollSpeed = 1.5;
          const deltaX = wheelEvent.deltaX * scrollSpeed;
          const deltaY = wheelEvent.deltaY * scrollSpeed;
          const scrollDelta = deltaX !== 0 ? deltaX : deltaY;

          target.scrollLeft += scrollDelta;
          updateScrollIndicators(target);
        }
      };

      const handleScroll = (e: Event) => {
        const target = e.currentTarget as HTMLElement;
        if (target.classList.contains("vision360-map-cards")) {
          updateScrollIndicators(target);
        }
      };

      const handleTouchStart = (e: Event) => {
        const touchEvent = e as TouchEvent;
        const target = e.currentTarget as HTMLElement;
        if (target.classList.contains("vision360-map-cards")) {
          const touch = touchEvent.touches[0];
          target.dataset.touchStartX = touch.clientX.toString();
          target.dataset.touchStartScrollLeft = target.scrollLeft.toString();
        }
      };

      const handleTouchMove = (e: Event) => {
        const touchEvent = e as TouchEvent;
        const target = e.currentTarget as HTMLElement;
        if (
          target.classList.contains("vision360-map-cards") &&
          target.dataset.touchStartX
        ) {
          e.preventDefault();
          const touch = touchEvent.touches[0];
          const startX = parseFloat(target.dataset.touchStartX);
          const startScrollLeft = parseFloat(
            target.dataset.touchStartScrollLeft || "0",
          );
          const deltaX = startX - touch.clientX;
          target.scrollLeft = startScrollLeft + deltaX;
        }
      };

      const handleTouchEnd = (e: Event) => {
        const target = e.currentTarget as HTMLElement;
        if (target.classList.contains("vision360-map-cards")) {
          delete target.dataset.touchStartX;
          delete target.dataset.touchStartScrollLeft;
        }
      };

      let cardContainer: HTMLElement | null = null;

      const addEventListeners = () => {
        cardContainer = document.querySelector(
          `.vision360-map-cards`,
        ) as HTMLElement;
        if (cardContainer) {
          cardContainer.addEventListener("wheel", handleWheel, {
            passive: false,
          });
          cardContainer.addEventListener("scroll", handleScroll, {
            passive: true,
          });
          cardContainer.addEventListener("touchstart", handleTouchStart, {
            passive: true,
          });
          cardContainer.addEventListener("touchmove", handleTouchMove, {
            passive: false,
          });
          cardContainer.addEventListener("touchend", handleTouchEnd, {
            passive: true,
          });

          updateScrollIndicators(cardContainer);
        }
      };

      const removeEventListeners = () => {
        if (cardContainer) {
          cardContainer.removeEventListener("wheel", handleWheel);
          cardContainer.removeEventListener("scroll", handleScroll);
          cardContainer.removeEventListener("touchstart", handleTouchStart);
          cardContainer.removeEventListener("touchmove", handleTouchMove);
          cardContainer.removeEventListener("touchend", handleTouchEnd);
        }
      };

      const timer = setTimeout(addEventListeners, 100);

      return () => {
        clearTimeout(timer);
        removeEventListeners();
      };
    }, [selectedMarker, updateScrollIndicators]);

    if (!isLoaded) {
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
          <span className="text-gray-400">Carregando mapa...</span>
        </div>
      );
    }

    if (cards.length === 0) {
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
          <span className="text-gray-400">Nenhum resultado encontrado</span>
        </div>
      );
    }

    return (
      <div className="relative w-full h-full">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={initialCenter}
          zoom={5}
          onLoad={handleMapLoad}
          onClick={handleCloseCardList}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {mapReady &&
            groupedMarkers.map((groupedMarker) => (
              <MemoizedMarker
                key={groupedMarker.id}
                groupedMarker={groupedMarker}
                animatedMarkerId={animatedMarkerId}
                onMarkerClick={handleMarkerClick}
                getMarkerIcon={getMarkerIcon}
              />
            ))}

          {centerCoords && (
            <>
              <Marker
                position={centerCoords}
                icon={getCenterMarkerIcon()}
                onClick={handleCenterMarkerClick}
                title="Ponto central da busca"
              />
              {showCenterInfoWindow && (
                <InfoWindow
                  position={centerCoords}
                  onCloseClick={() => setShowCenterInfoWindow(false)}
                >
                  <div className="p-2">
                    <div className="font-medium text-sm text-gray-800 mb-1">
                      Ponto central da localização informada
                    </div>
                    <div className="text-xs text-gray-600">
                      Localização de referência para esta busca, a distância é
                      calculada a partir deste ponto.
                    </div>
                  </div>
                </InfoWindow>
              )}
            </>
          )}
        </GoogleMap>

        <div className="absolute top-4 left-4 z-10">
          <Button
            onClick={handleTogglePartners}
            disabled={loadingPartners}
            className="bg-primary hover:bg-primary/90 text-black font-medium shadow-lg"
            size="default"
          >
            <Building2 className="w-4 h-4 mr-2" />
            {loadingPartners
              ? "Carregando..."
              : viewMode === "partners"
                ? "Voltar para Salas"
                : "Visualizar Parceiros"}
          </Button>
        </div>

        {selectedMarker && (
          <div
            className="absolute bottom-2 left-4 right-4"
            style={{ marginBottom: customBottomPosition }}
          >
            <div className="relative">
              <button
                onClick={handleCloseCardList}
                className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200 shadow-sm"
                title="Fechar"
              >
                <svg
                  className="w-3 h-3 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="relative overflow-hidden">
                {selectedMarker.cards.length > 1 && (
                  <button
                    onClick={() => scrollTo("left")}
                    className={`absolute top-1/2 -translate-y-1/2 left-2 w-6 h-6 bg-white/90 border border-black/10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10 shadow-md hover:bg-white hover:shadow-lg ${
                      !showScrollIndicators.left
                        ? "opacity-0 pointer-events-none"
                        : ""
                    }`}
                    title="Scroll para esquerda"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    >
                      <path d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}

                {selectedMarker.cards.length > 1 && (
                  <button
                    onClick={() => scrollTo("right")}
                    className={`absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 bg-white/90 border border-black/10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10 shadow-md hover:bg-white hover:shadow-lg ${
                      !showScrollIndicators.right
                        ? "opacity-0 pointer-events-none"
                        : ""
                    }`}
                    title="Scroll para direita"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    >
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}

                <div
                  className="flex gap-3 pb-2 vision360-map-cards scrollbar-none overscroll-x-contain max-w-full w-full transition-[cursor] duration-200"
                  style={{
                    overflowX:
                      selectedMarker.cards.length > 1 ? "auto" : "visible",
                    cursor:
                      selectedMarker.cards.length > 1 ? "grab" : "default",
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                    scrollBehavior: "auto",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMarker.cards.length > 1) {
                      e.currentTarget.style.cursor = "grab";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMarker.cards.length > 1) {
                      e.currentTarget.style.cursor = "default";
                    }
                  }}
                  onMouseDown={(e) => {
                    if (selectedMarker.cards.length > 1) {
                      e.currentTarget.style.cursor = "grabbing";
                    }
                  }}
                  onMouseUp={(e) => {
                    if (selectedMarker.cards.length > 1) {
                      e.currentTarget.style.cursor = "grab";
                    }
                  }}
                >
                  {selectedMarker.cards.map((card) => {
                    const cardIsSelected = isSelected(card.id);
                    return (
                      <CardItem
                        key={card.id}
                        card={card}
                        isSelected={cardIsSelected}
                        onToggleSelection={toggleSelection}
                        onOpenDrawer={onOpenDrawer}
                        includeRecentUpdates={includeRecentUpdates}
                        isPartnerMode={viewMode === "partners"}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

Vision360Map.displayName = "Vision360Map";

const CardItem = React.memo(
  ({
    card,
    isSelected,
    onToggleSelection,
    onOpenDrawer,
    includeRecentUpdates = false,
    isPartnerMode = false,
  }: {
    card: Card;
    isSelected: boolean;
    onToggleSelection: (id: string) => void;
    onOpenDrawer: (card: Card) => void;
    includeRecentUpdates?: boolean;
    isPartnerMode?: boolean;
  }) => {
    const shouldDisableSelection =
      isPartnerMode || (isCardOutdated(card) && !includeRecentUpdates);

    return (
      <div
        className={`flex-shrink-0 w-64 bg-white rounded-lg p-3 border shadow-lg transition-all duration-200 ${
          shouldDisableSelection
            ? isPartnerMode
              ? "border-yellow-200"
              : "border-orange-200"
            : isSelected
              ? "border-primary shadow-primary/20"
              : "border-gray-200 hover:border-gray-300"
        }`}
        onClick={
          shouldDisableSelection ? undefined : () => onToggleSelection(card.id)
        }
        style={{ cursor: shouldDisableSelection ? "default" : "pointer" }}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h4 className="font-medium text-sm text-gray-800 mb-1 line-clamp-2 min-h-[2.5rem]">
              {card.title}
            </h4>
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">
              {card.address}
            </p>
            {isPartnerMode && (
              <>
                {card.privateOfficesCount && card.privateOfficesCount > 0 ? (
                  <div className="bg-green-100 border border-green-300 rounded px-2 py-1 mb-2">
                    <p className="text-xs text-green-800 font-medium">
                      🏢 Parceiro com salas cadastradas
                    </p>
                    <p className="text-xs text-green-700 mt-0.5">
                      {card.privateOfficesCount}{" "}
                      {card.privateOfficesCount === 1 ? "sala" : "salas"}{" "}
                      disponíveis
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-100 border border-yellow-300 rounded px-2 py-1 mb-2">
                    <p className="text-xs text-yellow-800 font-medium">
                      🏢 Parceiro sem salas cadastradas
                    </p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      Oportunidade de prospecção
                    </p>
                  </div>
                )}
              </>
            )}
            {!isPartnerMode && shouldDisableSelection && (
              <div className="bg-orange-100 border border-orange-300 rounded px-2 py-1 mb-2">
                <p className="text-xs text-orange-800 font-medium">
                  ⚠️ Sem atualizações recentes
                </p>
                <p className="text-xs text-orange-700 mt-0.5">
                  Para selecionar, ative o filtro no painel
                </p>
              </div>
            )}
          </div>
          {!isPartnerMode && (
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-2 flex-shrink-0 ${
                shouldDisableSelection
                  ? "border-gray-300"
                  : isSelected
                    ? "bg-primary border-primary"
                    : "border-gray-300"
              }`}
            >
              {isSelected && !shouldDisableSelection && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
          )}
        </div>
        {!isPartnerMode && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onOpenDrawer(card);
            }}
            className="w-full bg-primary text-black hover:bg-primary/90"
            size="sm"
          >
            Ver detalhes
          </Button>
        )}
      </div>
    );
  },
);

CardItem.displayName = "CardItem";

const MemoizedMarker = React.memo(
  ({
    groupedMarker,
    animatedMarkerId,
    onMarkerClick,
    getMarkerIcon,
  }: {
    groupedMarker: GroupedMarker;
    animatedMarkerId: string | null;
    onMarkerClick: (groupedMarker: GroupedMarker) => void;
    getMarkerIcon: (
      groupedMarker: GroupedMarker,
      shouldAnimate: boolean,
    ) => any;
  }) => {
    const shouldAnimate = groupedMarker.cards.some(
      (card) => card.id === animatedMarkerId,
    );

    const icon = getMarkerIcon(groupedMarker, shouldAnimate);

    return (
      <Marker
        icon={icon}
        position={groupedMarker.position}
        onClick={() => onMarkerClick(groupedMarker)}
        animation={
          shouldAnimate ? window.google.maps.Animation.BOUNCE : undefined
        }
      />
    );
  },
);

MemoizedMarker.displayName = "MemoizedMarker";
